import { addLeadingSlash } from "@frend-digital/utils";
import { Err, Ok } from "@frend-digital/utils/results";

export const getLang = (locale: string) => {
  return locale.split("-")[0]!;
};

export interface Routing {
  locales: string[];
  defaultLocale: string;
  localePrefix?: "always" | "as-needed";
  domains?: Array<{
    domain: string;
    defaultLocale: string;
    locales: string[];
    /**
     * Which protocol to use for the domain
     * @default "https"
     */
    protocol?: string;
  }>;
}

export const defineRouting = <T extends Routing>(config: T) => {
  return {
    ...config,
    localePrefix: config.localePrefix || "as-needed",
  };
};

/**
 * Finds the preferred geo (en-US, en-GB, en-NO) for the given locale (en, nb, no), it finds the first result based on preferred countries order
 * @param language - The locale to find the geo for
 * @param preferredCountries - The preferred countries to find the geo for
 */
export function findGeoByLanguage(
  language: string,
  routing: Routing,
  preferredCountries: string[] = [],
): string | undefined {
  const geos = routing.locales.filter((locale) =>
    locale.startsWith(`${language}-`),
  );

  // Sort by preferred countries
  geos.sort((a, b) => {
    const countryA = a.split("-")[1];
    const countryB = b.split("-")[1];
    const indexA = preferredCountries.indexOf(countryA!);
    const indexB = preferredCountries.indexOf(countryB!);

    // If a country is in the preferred list, it gets a higher priority
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB; // Sort based on order in preferredCountries
    } else if (indexA !== -1) {
      return -1; // a is preferred, so it comes first
    } else if (indexB !== -1) {
      return 1; // b is preferred, so it comes first
    } else {
      return 0; // Neither is preferred, keep the original order
    }
  });

  return geos[0]; // Return the first element in the sorted list
}

export type GeoLocale = `${Lowercase<string>}-${string}`;

export const parseGeo = (geo: string) => {
  const [language, country] = geo.split("-");

  if (!language || !country) {
    return Err({
      message: "Invalid geo format",
      cause: geo,
    });
  }

  return Ok({ language: language, country });
};

export const LOCALE_PATH_REGEX =
  /\/(?<language>[a-z]{2})(?:-(?<country>[a-z]{2}))?(?=\/|$)/i;

export const LOCALE_REGEX =
  /^(?<language>[a-z]{2})(?:-(?<country>[a-z]{2}))?$/i;

function applyLocale(url: string, locale: string) {
  const matches = LOCALE_PATH_REGEX.test(url);
  if (matches) {
    return url.replace(LOCALE_PATH_REGEX, `/${locale}`);
  }
  return `/${locale}${addLeadingSlash(url)}`;
}

export function findDomain(
  locale: string,
  routing: Pick<Routing, "domains" | "defaultLocale">,
) {
  if (routing.domains) {
    const domain = routing.domains.find(
      (domain) =>
        domain.defaultLocale === locale || domain.locales.includes(locale),
    );
    if (domain) return domain;

    return routing.domains.find(
      (domain) => domain.defaultLocale === routing.defaultLocale,
    );
  }
}

export function isDefaultLocale(
  locale: string,
  routing: Pick<Routing, "defaultLocale" | "domains">,
) {
  const domain = findDomain(locale, routing);
  if (domain) {
    if (domain.defaultLocale === locale) {
      return true;
    }
  }

  return locale === routing.defaultLocale;
}

/**
 * A replacer function that first checks if there is a locale present in the URL. If so it will not replace it. If there is no locale present it will add the locale to the URL.
 * @param url - The url to replace the locale in
 * @param locale - The locale to replace the url with
 * @returns {string} - The modified url
 */
export const replaceLocale = (
  url: string,
  locale: string,
  routing: Routing,
  overrideLocale?: string,
): string => {
  // Quick check for external URLs using string methods
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("//")
  ) {
    return url;
  }

  if (overrideLocale) {
    return applyLocale(url, overrideLocale);
  }

  const matches = LOCALE_PATH_REGEX.test(url);
  if (matches) {
    if (overrideLocale) {
      return url.replace(LOCALE_PATH_REGEX, `/${overrideLocale}`);
    }
    return url;
  }

  if (routing.localePrefix === "as-needed") {
    if (isDefaultLocale(locale, routing)) {
      return url;
    }
  }

  const preferredLocale = overrideLocale || locale;

  // If there is no locale present in the URL, add it
  return `/${preferredLocale}${addLeadingSlash(url)}`;
};

export const safeLocale = <T extends string | undefined>(
  routing: Routing,
  locale: T,
) => {
  if (!locale) {
    return locale;
  }
  if (!routing.locales.includes(locale)) {
    return routing.defaultLocale;
  }
  return locale;
};
